// tegn-panel.js – ét samlet tegn-panel over alle tegn der oversættes på mere end én måde.
// Erstatter det tidligere 为-panel (ejer-beslutning 2026-07-31: 为 er ét tegn blandt de øvrige).
//
// BÆRENDE BESLUTNING – hvorfor tegn-dataene IKKE ligger i Yjs-dokumentet:
// 为-sporet lagde sine 110 par som ankrede annotationer i Yjs-dokumentet. Med 10.279
// forekomster ville samme model give et DO-snapshot på flere MB, og en SQLite-backet
// Durable Object har et hårdt loft på 2 MB pr. værdi (nøgle+værdi tilsammen). Tegn-dataene
// hentes derfor som en statisk analysefil (GET /api/tegn, bag session) og opløses mod de
// LEVENDE segment-ankre der allerede findes i dokumentets `segments`-array; kortlægningen
// fra analysens offsets til den redigerede segment-tekst sker i tegn-resolve.js. Arbejdet
// er dermed bundet til det ÅBNE tegn, ikke til alle 10.279 forekomster.
//
// CACHE-DISCIPLIN (samme som 为-panelet efter gate-runden 2026-07-29): DOM'en genopbygges
// kun når INDHOLDET ændrer sig, og cache-nøglen bærer ALDRIG positioner – det var netop den
// fejl gate-runden rettede (hvert tastetryk rev alle rækker ned). Indekset og den åbne
// liste har HVER SIN nøgle, så tastning i et urørt segment hverken rebuilder indekset
// eller listen. Antal faktiske DOM-genopbygninger eksponeres i panel.dataset.tegnRebuilds
// (starter på '0' FØR første render), så en browser-test kan aflæse den udefra.

import { csvText } from './vendor/csv/index.js';
import { TEGN_CSV_KOLONNER, tegnCsvRows } from './tegn-csv-kolonner.js';
import { mapRange } from './tegn-resolve.js';

function el(tag, cls, txt) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (txt != null) e.textContent = txt;
  return e;
}

const STATUS_RANG = { uaendret: 0, flyttet: 1, redigeret: 2, vaek: 3 };
const TXT_FJERNET = '(fjernet i teksten)';
const TXT_INGEN_DA = '(ingen dansk gengivelse)';
// Nøgle-sentineler (samme trick som 为-panelets NO_DA): en bruger kunne redigere et dansk
// span til ordret at lyde »(ingen dansk gengivelse)«, så den VISTE tekst kan ikke bruges som
// nøgle-repræsentation for »mangler«/»væk«. U+0000 kan ikke tastes og findes ikke i teksten.
// Skrevet som escapes, aldrig literaler: et kontroltegn i kilden er usynligt i en diff.
const K_VAEK = '\u0000V';
const K_MANGLER = '\u0000M';
const US = '\u001f'; // felt-skilletegn i cache-nøglerne

/**
 * Passer den hentede analysefil til DETTE dokument?
 *
 * data/tegn.json bærer `alignmentVersion` + `sourceHash`, og Yjs-dokumentets `meta`-map bærer
 * de samme to felter (sat af worker/realtime/import-alignment.js). Er de uenige, opløses
 * analysens offsets mod en HELT anden tekst: målt worst case ville 14,7 % af rækkerne stå
 * umarkerede og klikbare men pege på tilfældig tekst, og 64,5 % blive vist som »redigeret« –
 * altså tilskrive en deploy-fejl brugerens egne redigeringer. Panelets tre statusser
 * (uaendret/redigeret/vaek) kan ikke udtrykke »datafilen passer ikke til dokumentet«, så
 * vagten hører til FØR rækkerne bygges. Uenigheden opstår når `DOC_ID` bumpes uden at
 * tegn-sporet køres om (eller omvendt) – to adskilte handlinger i docs/reanalyse-runbook.md.
 *
 * FEJLER LUKKET: et manglende felt på den ene side er uenighed, aldrig enighed. Versionerne
 * sammenlignes som strenge, fordi et versionsnummer er en identitet og ikke en type (JSON
 * kan levere 4 eller "4"); to FORSKELLIGE versioner er stadig uenighed.
 *
 * Ren funktion uden DOM og uden Yjs, så tests/test-tegn-guard.mjs kan prøve den direkte.
 * @param {object} data det hentede /api/tegn
 * @param {object} meta {alignmentVersion, sourceHash} læst af dokumentets meta-map
 * @returns {{ok: boolean, grund?: string}} grund er en dansk delsætning til fejlbeskeden
 */
export function analysePasserTilDokument(data, meta) {
  const d = data || {};
  const m = meta || {};
  if (d.alignmentVersion == null || m.alignmentVersion == null) {
    return {
      ok: false,
      grund: 'alignment-versionen mangler (analysen: ' + (d.alignmentVersion == null ? 'ingen' : d.alignmentVersion)
        + ', dokumentet: ' + (m.alignmentVersion == null ? 'ingen' : m.alignmentVersion) + ')',
    };
  }
  if (String(d.alignmentVersion) !== String(m.alignmentVersion)) {
    return { ok: false, grund: 'analysen er alignment v' + d.alignmentVersion + ', dokumentet er v' + m.alignmentVersion };
  }
  if (!d.sourceHash || !m.sourceHash) {
    return {
      ok: false,
      grund: 'kildehashen mangler (analysen: ' + (d.sourceHash ? 'sat' : 'ingen')
        + ', dokumentet: ' + (m.sourceHash ? 'sat' : 'ingen') + ')',
    };
  }
  if (String(d.sourceHash) !== String(m.sourceHash)) {
    return { ok: false, grund: 'kildehashen er en anden (analysen: ' + String(d.sourceHash).slice(0, 20) + '…)' };
  }
  return { ok: true };
}

/**
 * Montér tegn-panelet.
 * @param {object} opts
 *   doc            Y.Doc (til resolveAnchor)
 *   segments       Y.Array med de levende segmenter ({id, zhAnchor, daAnchor})
 *   resolveAnchor  (anchor, doc) -> [from, to] | null
 *   zhView, daView CodeMirror EditorViews (til at læse den levende tekst + linjenumre)
 *   farve          dimensionsfarven (#AA4499)
 *   onFlash        (lang, from, to) -> ruller til og blinker forekomsten op i editoren
 *   requestRender  () -> bed editoren om en recompute (fører til sync()-kald)
 * @returns {{el: HTMLElement, sync: () => Array<{lang: string, from: number, to: number}>, destroy: () => void}}
 */
export function mountTegnPanel({ doc, segments, resolveAnchor, zhView, daView, farve, onFlash, requestRender }) {
  // ---- tilstand ----
  let data = null;           // indholdet af data/tegn.json når hentet
  let fetchPromise = null;
  let open = '';             // det åbne tegn ('' = intet åbent; altid højst ét)
  let scope = 'flertydige';  // 'flertydige' (g >= 2, default) | 'gentagne' (n >= 2)
  let sortMode = 'gengivelser';
  let filter = '';
  let rebuilds = 0;
  let indexKey = null;       // null: indekset er aldrig bygget
  let listKey = '';          // '': den tomme liste er allerede "renderet" (skelettet er tomt)
  let chipEls = new Map();   // tegn -> chip-element (til .active-toggling uden rebuild)
  let revokeTimer = 0;
  const metaMap = doc.getMap('meta'); // dokumentets egen alignmentVersion + sourceHash
  let vagtOk = null;         // null: endnu ikke afgjort (dokumentet er ikke synket); true/false: dommen

  // ---- skelet (oppefra og ned: hoved, status, åben liste, chip-indeks nederst) ----
  const panel = el('section', 'tegn-panel');
  panel.style.setProperty('--tegn', farve);
  panel.dataset.tegnAktiv = '';
  panel.dataset.tegnRebuilds = '0'; // sat FØR første render, så attributten altid kan aflæses

  const head = el('div', 'tegn-head');
  head.appendChild(el('span', 'tegn-title', 'Tegn · gengivelser'));
  const count = el('span', 'tegn-count', '0');
  const actions = el('div', 'tegn-actions');

  const filterInput = el('input', 'tegn-filter');
  filterInput.type = 'text';
  filterInput.placeholder = 'Filtrér tegn …';

  const sortSel = el('select', 'tegn-sort');
  sortSel.appendChild(new Option('Flest gengivelser', 'gengivelser'));
  sortSel.appendChild(new Option('Flest forekomster', 'antal'));
  sortSel.appendChild(new Option('Første i teksten', 'tekst'));

  const scopeBtn = el('button', 'ghost-btn tegn-scope', 'Kun flertydige');
  scopeBtn.type = 'button';
  const dlOne = el('button', 'ghost-btn tegn-download-one', 'Hent CSV (dette tegn)');
  dlOne.type = 'button';
  dlOne.disabled = true; // aktiveres først når et tegn er åbent
  const dlAll = el('button', 'ghost-btn tegn-download-all', 'Hent CSV (alle tegn)');
  dlAll.type = 'button';
  const foldBtn = el('button', 'ghost-btn tegn-fold', 'Fold ud');
  foldBtn.type = 'button';
  actions.append(filterInput, sortSel, scopeBtn, dlOne, dlAll, foldBtn);
  head.append(count, actions);

  const statusEl = el('div', 'tegn-status');
  statusEl.hidden = true;
  const list = el('div', 'tegn-list');
  const index = el('div', 'tegn-index');
  panel.append(head, statusEl, list, index);

  // Panelet starter SAMMENFOLDET: /api/tegn hentes dovent – første gang panelet foldes ud
  // eller første gang »Hent CSV (alle tegn)« klikkes – aldrig ved sideindlæsning. Et
  // udfoldet start-panel ville tvinge hentningen med det samme og gøre dovenskaben til pynt.
  panel.classList.add('folded');

  // ---- datahentning (doven, med synlig indlæsnings- og fejltilstand) ----
  function setStatus(txt, error) {
    statusEl.hidden = !txt;
    statusEl.classList.toggle('tegn-status-error', !!error);
    statusEl.textContent = '';
    if (!txt) return;
    statusEl.appendChild(el('span', null, txt));
    if (error) {
      const retry = el('button', 'ghost-btn tegn-retry', 'Prøv igen');
      retry.type = 'button';
      retry.addEventListener('click', () => ensureData());
      statusEl.appendChild(retry);
    }
  }

  // Er analysefilen og dokumentet uenige, vises INGEN rækker – de ville pege på tilfældig
  // tekst. Se analysePasserTilDokument() ovenfor for hvorfor og hvor tallene kommer fra.
  function analyseKlar() { return !!data && vagtOk === true; }

  function vurderVagt() {
    if (!data) return; // intet at sammenligne med endnu
    // `meta` fyldes først når den første sync lander, så et TOMT meta betyder »dokumentet er
    // ikke synket endnu«, ikke »uenighed« – ellers ville vagten fyre falsk ved hver
    // sideindlæsning. Der ventes derfor på alignmentVersion (samme felt buildInitialDoc selv
    // gater på) og re-evalueres når meta ændrer sig. Er feltet der, dømmes der hårdt: et
    // manglende sourceHash er da uenighed, ikke enighed – vagten fejler LUKKET.
    if (metaMap.get('alignmentVersion') == null) { vagtOk = null; return; }
    const dom = analysePasserTilDokument(data, {
      alignmentVersion: metaMap.get('alignmentVersion'),
      sourceHash: metaMap.get('sourceHash'),
    });
    if (vagtOk === dom.ok) return; // uændret dom: rør hverken status eller DOM
    vagtOk = dom.ok;
    if (dom.ok) setStatus('', false);
    else {
      // Luk et evt. åbent tegn: der må ikke stå én eneste række der peger på tilfældig tekst.
      open = '';
      panel.dataset.tegnAktiv = '';
      dlOne.disabled = true;
      setStatus('Tegn-analysen passer ikke til dette dokument (' + dom.grund + '). Analysen er '
        + 'lavet på en anden tekstversion end dokumentet – kør tegn-sporet om, eller deploy den '
        + 'tegn.json der hører til dette dokument.', true);
      renderList([], 0);
    }
    renderIndex();
  }

  function ensureData() {
    // Er data hentet, er »prøv igen« en fornyet vurdering af vagten (dokumentet kan være
    // synket imens); selve filen hentes aldrig to gange.
    if (data) { vurderVagt(); return Promise.resolve(); }
    if (fetchPromise) return fetchPromise;
    setStatus('Henter tegn-analysen …', false);
    fetchPromise = fetch('/api/tegn')
      .then(async (r) => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        data = await r.json();
        vurderVagt(); // sætter status: tom ved enighed, fejltilstand ved uenighed
        renderIndex();
        requestRender(); // no-op for dekorationerne når intet tegn er åbent, men billigt
      })
      .catch((e) => {
        fetchPromise = null; // gør »Prøv igen« mulig
        setStatus('Kunne ikke hente tegn-analysen (' + (e && e.message ? e.message : 'ukendt fejl') + ').', true);
      });
    return fetchPromise;
  }

  // ---- indekset (chips nederst) ----
  function segOrderMap() {
    // Dokument-rækkefølgen af segmenterne, læst af det LEVENDE segments-array. Bruges til
    // »Første i teksten«-sorteringen; et segment der ikke længere findes falder bagest.
    const m = new Map();
    segments.forEach((seg, i) => { if (seg && seg.id != null && !m.has(seg.id)) m.set(seg.id, i); });
    return m;
  }

  function tekstNoegle(info, segOrder) {
    const f0 = info && Array.isArray(info.f) ? info.f[0] : null;
    if (!f0) return [Number.MAX_SAFE_INTEGER, 0, 0, ''];
    const so = segOrder.has(f0[0]) ? segOrder.get(f0[0]) : Number.MAX_SAFE_INTEGER;
    return [so, f0[1] || 0, f0[2] || 0, String(f0[0])];
  }

  const efterTekst = (a, b) =>
    a.tk[0] - b.tk[0] || a.tk[1] - b.tk[1] || a.tk[2] - b.tk[2] ||
    (a.tk[3] < b.tk[3] ? -1 : a.tk[3] > b.tk[3] ? 1 : 0);

  function indexEntries() {
    if (!analyseKlar() || !data.tegn) return [];
    const segOrder = segOrderMap();
    const valgte = [];
    for (const ch of Object.keys(data.tegn)) {
      const info = data.tegn[ch] || {};
      const n = info.n != null ? info.n : (Array.isArray(info.f) ? info.f.length : 0);
      const g = info.g || 0;
      const u = info.u || 0;
      if (scope === 'flertydige' ? g < 2 : n < 2) continue;
      // Filtret matcher på tegnet SELV: alle tegn der optræder i filter-strengen vises,
      // så man både kan taste ét tegn og indsætte en hel passage at slå op fra.
      if (filter && [...filter].indexOf(ch) < 0) continue;
      valgte.push({ ch, n, g, u, tk: tekstNoegle(info, segOrder) });
    }
    if (sortMode === 'antal') valgte.sort((a, b) => b.n - a.n || b.g - a.g || efterTekst(a, b));
    else if (sortMode === 'tekst') valgte.sort(efterTekst);
    else valgte.sort((a, b) => b.g - a.g || b.n - a.n || efterTekst(a, b));
    return valgte;
  }

  function renderIndex() {
    // Indeksets egen cache-nøgle: kun UI-tilstand + om data findes – aldrig dokumenttekst
    // og aldrig positioner. Tastning i dokumentet kan derfor ikke rebuilde indekset.
    // Det åbne tegn er bevidst IKKE med: .active toggles imperativt i setOpen.
    // Vagtens dom er en af FIRE tilstande, ikke to: »hentet, men dokumentet er ikke synket
    // endnu« må ikke dele nøgle med »hentet og enig«, ellers udebliver rebuilden når vagten
    // flipper til enig og indekset ville stå tomt.
    const tilstand = !data ? 'ingen' : vagtOk === true ? 'data' : vagtOk === false ? 'uenig' : 'venter';
    const key = [scope, sortMode, filter, tilstand].join(US);
    if (key === indexKey) return;
    indexKey = key;
    rebuilds++;
    panel.dataset.tegnRebuilds = String(rebuilds);
    const entries = indexEntries();
    count.textContent = String(entries.length);
    index.innerHTML = '';
    chipEls = new Map();
    if (!entries.length) {
      index.appendChild(el('div', 'tegn-empty',
        !data ? 'Tegn-analysen er ikke hentet endnu'
          : vagtOk === false ? 'Tegn-analysen passer ikke til dokumentet'
            : vagtOk === null ? 'Afventer dokumentets synkronisering …'
              : (filter ? 'Ingen tegn matcher filtret' : 'Ingen tegn i analysen endnu')));
      return;
    }
    for (const e of entries) {
      const chip = el('button', 'tegn-chip');
      chip.type = 'button';
      chip.dataset.tegn = e.ch;
      chip.title = e.n + ' forekomster · ' + e.g + ' gengivelser' + (e.u ? ' · ' + e.u + ' uden' : '');
      chip.appendChild(el('span', 'tegn-chip-char', e.ch));
      chip.appendChild(el('span', 'tegn-chip-n', e.n + '·' + e.g));
      if (e.ch === open) chip.classList.add('active');
      chip.addEventListener('click', () => setOpen(e.ch));
      chipEls.set(e.ch, chip);
      index.appendChild(chip);
    }
  }

  function setOpen(ch) {
    // Klik på det åbne tegns chip lukker det (toggle); klik på et andet tegn erstatter det
    // åbne – der er altid højst ÉN åben liste.
    open = open === ch ? '' : ch;
    panel.dataset.tegnAktiv = open;
    chipEls.forEach((c, key) => c.classList.toggle('active', key === open));
    dlOne.disabled = !open;
    requestRender(); // editoren kører recompute -> sync() -> liste + fremhævning i ruderne
  }

  // ---- opløsning af forekomster mod den levende tekst ----
  function buildSegMap() {
    const m = new Map();
    segments.forEach((seg) => { if (seg && seg.id != null && !m.has(seg.id)) m.set(seg.id, seg); });
    return m;
  }

  // Opløs én forekomst [segId, enhedsIndex, offsetIEnheden]. En side er
  //   { ok: true, status, text, from, to }   (from/to = ABSOLUTTE dokument-positioner)
  //   { ok: false, status: 'vaek', ... }     (anker kollapset / passage væk)
  //   { missing: true }                      (da-siden når enheden ingen gengivelse har, do === -1)
  // Positionerne bruges ved KLIK, til fremhævning og ved eksport – aldrig i cache-nøgler.
  function resolveOcc(occ, segMap) {
    const segId = occ[0];
    const segData = (data.segmenter || {})[segId];
    const unit = segData && Array.isArray(segData.e) ? segData.e[occ[1]] : null;
    if (!unit) return null; // forekomstliste og segmentdata ude af trit; burde ikke ske
    const liveSeg = segMap.get(segId) || null;

    const side = (anchor, view, origText, off, len) => {
      if (!liveSeg || !anchor) return { ok: false, status: 'vaek', text: '', from: null, to: null };
      const abs = resolveAnchor(anchor, doc);
      if (!abs) return { ok: false, status: 'vaek', text: '', from: null, to: null };
      const liveText = view.state.doc.sliceString(abs[0], abs[1]);
      const m = mapRange(origText || '', liveText, off, off + len);
      if (m.status === 'vaek') return { ok: false, status: 'vaek', text: '', from: null, to: null };
      return { ok: true, status: m.status, text: liveText.slice(m.from, m.to), from: abs[0] + m.from, to: abs[0] + m.to };
    };

    return {
      unit,
      zh: side(liveSeg && liveSeg.zhAnchor, zhView, segData.zh, unit.zo, (unit.zh || '').length),
      da: (unit.do == null || unit.do < 0)
        ? { missing: true }
        : side(liveSeg && liveSeg.daAnchor, daView, segData.da, unit.do, (unit.da || '').length),
    };
  }

  // Returnerer { rows, udenData }. udenData er de forekomster resolveOcc gav null for
  // (segmentet eller enheden mangler i analysefilen). De blev tidligere sprunget over TAVST,
  // og fordi listens hoved viser analysens tal (info.n) og ikke rows.length, var et drop
  // usynligt. De tælles nu og vises i hovedet – en post der springes over uden at blive talt,
  // gør resultatet grønt for netop det den ikke så.
  function collectRows(ch) {
    const info = data && data.tegn ? data.tegn[ch] : null;
    if (!info || !Array.isArray(info.f)) return { rows: [], udenData: 0 };
    const segMap = buildSegMap();
    const rows = [];
    let udenData = 0;
    for (const occ of info.f) {
      const r = resolveOcc(occ, segMap);
      if (!r) { udenData++; continue; }
      const zhS = r.zh.ok ? r.zh.status : 'vaek';
      const daS = r.da.missing ? null : (r.da.ok ? r.da.status : 'vaek');
      const vaerste = daS == null || STATUS_RANG[zhS] >= STATUS_RANG[daS] ? zhS : daS;
      rows.push({
        occ, segId: occ[0], ci: occ[2], unit: r.unit, zh: r.zh, da: r.da,
        fn: r.unit.fn || '', note: r.unit.n || '',
        status: vaerste,
        // display styrer række-klassen: 'uaendret' og 'flyttet' foldes bevidst sammen til ''
        // (ingen visuel forskel), så et tastetryk FØR en enhed i samme segment – som kun
        // flytter positioner – ikke ændrer cache-nøglen og river rækkerne ned.
        display: vaerste === 'vaek' ? 'vaek' : vaerste === 'redigeret' ? 'redigeret' : '',
      });
    }
    return { rows, udenData };
  }

  // ---- den åbne liste ----
  function listNoegle(rows, udenData) {
    // Ren INDHOLDS-nøgle: tekster, funktions-etiket, note og display-klasse.
    // ALDRIG positioner (from/to/linjenumre) – det var fejlen gate-runden 2026-07-29 rettede.
    // Antallet af droppede forekomster står i hovedet og hører derfor med i nøglen.
    if (!open) return '';
    return open + US + (udenData || 0) + '\n' + rows.map((r) => [
      r.segId, r.occ[1], r.ci,
      r.zh.ok ? r.zh.text : K_VAEK,
      r.da.missing ? K_MANGLER : (r.da.ok ? r.da.text : K_VAEK),
      r.fn, r.note, r.display,
    ].join(US)).join('\n');
  }

  function medMark(btn, text, ci, ch) {
    // Uændret/flyttet enhed: tegnet står på analysens offset ci (UTF-16-kodeenheder, samme
    // enhed som analysen måler i). Redigeret enhed: cellen viser den LEVENDE tekst, så find
    // tegnet dér; står det slet ikke i den redigerede tekst, vises cellen uden mark
    // (hellere ærligt umarkeret end markeret forkert).
    const idx = text.slice(ci, ci + ch.length) === ch ? ci : text.indexOf(ch);
    if (idx < 0) { btn.textContent = text; return; }
    btn.append(
      document.createTextNode(text.slice(0, idx)),
      el('mark', 'tegn-hit', ch),
      document.createTextNode(text.slice(idx + ch.length)),
    );
  }

  // Klik opløser positionen NU (frisk mapRange), så lytteren aldrig bærer render-tidens
  // positioner – samme princip som 为-panelets jumpToWeiPair efter gate-runden 2026-07-29.
  function springTil(lang, occ) {
    const r = resolveOcc(occ, buildSegMap());
    if (!r) return;
    const s = lang === 'zh' ? r.zh : r.da;
    if (!s || s.missing || !s.ok) return; // væk imens: gør intet; næste render omklassificerer
    onFlash(lang, s.from, s.to);
  }

  function buildRow(r) {
    const row = el('div', 'tegn-row');
    if (r.display) row.classList.add('tegn-row-' + r.display);
    if (r.note) row.title = r.note;
    if (r.zh.ok) {
      const b = el('button', 'tegn-cell tegn-cell-zh');
      b.type = 'button';
      medMark(b, r.zh.text, r.ci, open);
      b.addEventListener('click', () => springTil('zh', r.occ));
      row.appendChild(b);
    } else {
      row.appendChild(el('span', 'tegn-cell tegn-cell-zh tegn-missing', TXT_FJERNET));
    }
    if (r.da.missing) {
      row.appendChild(el('span', 'tegn-cell tegn-cell-da tegn-missing', TXT_INGEN_DA));
    } else if (r.da.ok) {
      const b = el('button', 'tegn-cell tegn-cell-da', r.da.text);
      b.type = 'button';
      b.addEventListener('click', () => springTil('da', r.occ));
      row.appendChild(b);
    } else {
      row.appendChild(el('span', 'tegn-cell tegn-cell-da tegn-missing', TXT_FJERNET));
    }
    // Funktions-etiketten er ikke klikbar (den peger ikke på en position i teksten);
    // notens fulde begrundelse ligger som tooltip.
    const fnCell = el('span', 'tegn-cell tegn-cell-fn', r.fn || '–'); // en-dash når tom
    if (!r.fn) fnCell.classList.add('tegn-missing');
    if (r.note) fnCell.title = r.note;
    row.appendChild(fnCell);
    return row;
  }

  function renderList(rows, udenData) {
    const key = listNoegle(rows, udenData);
    if (key === listKey) return;
    listKey = key;
    rebuilds++;
    panel.dataset.tegnRebuilds = String(rebuilds);
    list.innerHTML = '';
    if (!open) return; // tom liste når intet tegn er åbent
    const info = (data.tegn || {})[open] || {};
    let nRed = 0, nVaek = 0;
    for (const r of rows) {
      if (r.display === 'redigeret') nRed++;
      else if (r.display === 'vaek') nVaek++;
    }
    // Antal redigerede/fjernede vises SYNLIGT i hovedet frem for at fejle tavst.
    let headTxt = open + ' · ' + (info.n != null ? info.n : rows.length) + ' forekomster · '
      + (info.g || 0) + ' gengivelser · ' + (info.u || 0) + ' uden';
    if (nRed) headTxt += ' · ' + nRed + ' redigeret';
    if (nVaek) headTxt += ' · ' + nVaek + ' fjernet';
    // Forekomster analysefilen ikke har data for. Vises kun når der ER nogen, men så altid:
    // uden tælleren ville forskellen mellem info.n og antallet af rækker være usynlig.
    if (udenData) headTxt += ' · ' + udenData + ' uden data';
    list.appendChild(el('div', 'tegn-list-head', headTxt));
    const cols = el('div', 'tegn-cols');
    cols.append(el('span', null, '中文'), el('span', null, 'Dansk'), el('span', null, 'Funktion'));
    list.appendChild(cols);
    const body = el('div', 'tegn-body');
    if (!rows.length) body.appendChild(el('div', 'tegn-empty', 'Ingen forekomster'));
    for (const r of rows) body.appendChild(buildRow(r));
    list.appendChild(body);
  }

  /**
   * Kaldes af editorens recompute (hvert dekorations-gennemløb). Holder den åbne liste ajour
   * (cache-gated) og returnerer fremhævningerne for det ÅBNE tegn: zh-enheden og dens danske
   * gengivelse pr. forekomst. Intet tegn åbent (eller data ikke hentet) = tom liste og ingen
   * fremhævning – arbejdet er bundet til det åbne tegn.
   */
  function sync() {
    if (!analyseKlar() || !open) { renderList([], 0); return []; }
    const { rows, udenData } = collectRows(open);
    renderList(rows, udenData);
    const ud = [];
    for (const r of rows) {
      if (r.zh.ok) ud.push({ lang: 'zh', from: r.zh.from, to: r.zh.to });
      if (!r.da.missing && r.da.ok) ud.push({ lang: 'da', from: r.da.from, to: r.da.to });
    }
    return ud;
  }

  // ---- CSV-eksport ----
  // Serialiseringen (skilletegn, kvotering, formel-guard, BOM) bor i det VENDOREDE modul
  // frontend/src/vendor/csv/index.js; kolonnerne + kortlægningen i tegn-csv-kolonner.js.
  // Profil 'sheet' = semikolon + UTF-8-BOM: modtageren er et regneark, og Excel med dansk
  // locale deler på semikolon og gengiver kun æøå + de kinesiske tegn korrekt med BOM.
  function eksportRaekker(ch) {
    // Linjenumre beregnes KUN her, ved eksport-tidspunktet – de er positions-afledte og må
    // aldrig indgå i en cache-nøgle. Ved 'vaek' skrives analysens tekst i cellen i stedet
    // for en tom celle: status-kolonnen siger hvorfor, og rækken forbliver informativ.
    return collectRows(ch).rows.map((r) => ({
      tegn: ch,
      segment: r.segId,
      kinesisk: r.zh.ok ? r.zh.text : (r.unit.zh || ''),
      dansk: r.da.missing ? null : (r.da.ok ? r.da.text : (r.unit.da || '')),
      funktion: r.fn,
      note: r.note,
      status: r.status,
      zhLine: r.zh.ok ? zhView.state.doc.lineAt(r.zh.from).number : null,
      daLine: !r.da.missing && r.da.ok ? daView.state.doc.lineAt(r.da.from).number : null,
    }));
  }

  function hentCsv(filnavn, raekker) {
    const tekst = csvText(tegnCsvRows(raekker), TEGN_CSV_KOLONNER, { profile: 'sheet' });
    const blob = new Blob([tekst], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filnavn;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Frigiv først når browseren har læst blobben: et revoke i samme tick afbryder downloaden.
    clearTimeout(revokeTimer);
    revokeTimer = setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  const dato = () => new Date().toISOString().slice(0, 10);
  dlOne.addEventListener('click', () => {
    if (!open || !analyseKlar()) return;
    hentCsv('kyw-tegn-' + open + '-' + dato() + '.csv', eksportRaekker(open));
  });
  dlAll.addEventListener('click', () => {
    ensureData().then(() => {
      if (!analyseKlar() || !data.tegn) return; // uenig vagt: eksportér aldrig rækker der peger forkert
      // ALLE tegn i analysen – uanset scope/filter, knappen siger »alle« – i tekst-rækkefølge
      // (første forekomst), forekomsterne i deres egen tekst-orden.
      const segOrder = segOrderMap();
      const alle = Object.keys(data.tegn).map((ch) => ({ ch, tk: tekstNoegle(data.tegn[ch], segOrder) }));
      alle.sort(efterTekst);
      const raekker = [];
      for (const e of alle) raekker.push(...eksportRaekker(e.ch));
      hentCsv('kyw-tegn-alle-' + dato() + '.csv', raekker);
    });
  });

  // ---- hoved-kontroller ----
  filterInput.addEventListener('input', () => {
    filter = filterInput.value.trim();
    renderIndex();
  });
  sortSel.addEventListener('change', () => {
    sortMode = sortSel.value;
    renderIndex();
  });
  scopeBtn.addEventListener('click', () => {
    scope = scope === 'flertydige' ? 'gentagne' : 'flertydige';
    scopeBtn.textContent = scope === 'flertydige' ? 'Kun flertydige' : 'Alle gentagne';
    // Det åbne tegn lukkes IKKE selv om dets chip falder ud af det viste udsnit:
    // scope/filter er til at finde rundt i indekset, ikke en tilstand listen skal følge.
    renderIndex();
  });
  foldBtn.addEventListener('click', () => {
    const folded = panel.classList.toggle('folded');
    foldBtn.textContent = folded ? 'Fold ud' : 'Fold sammen';
    if (!folded) ensureData();
  });

  // `meta` er tom indtil den første sync lander, så vagten kan ikke dømme ved montering.
  // Den re-evalueres derfor hver gang meta ændrer sig; det er den enkleste form der hverken
  // kan fyre falsk på et usynket dokument eller lade en uenighed stå uopdaget.
  const paaMeta = () => vurderVagt();
  metaMap.observe(paaMeta);

  return {
    el: panel,
    sync,
    destroy() { clearTimeout(revokeTimer); metaMap.unobserve(paaMeta); },
  };
}
